import React, { useMemo } from 'react'
import { Box, Typography } from '@mui/material'
import StatusBadge from '../StatusBadge/StatusBadge'
import { useAppSettings } from '../../state/providers/AppSettingsProvider'

type BadgeRailVariant = 'card' | 'list'

type Availability = 'available' | 'sold_out' | 'not_ready'

type BadgePlacement = 'overlay' | 'number_column'

interface BeerBadgesRailProps {
  variant: BadgeRailVariant
  placement?: BadgePlacement
  availability?: Availability
  isInTastingList?: boolean
  onToggleTastingList?: () => void
  rating?: number
  showRatingWhenZero?: boolean
  demoRatingValue?: number
  isFavourite?: boolean
  onToggleFavourite?: () => void
  favouriteAriaLabel?: string
  onOpenRating?: () => void
  onOpenNotes?: () => void
  notesHasContent?: boolean
  onAvailabilityClick?: () => void
}

const BeerBadgesRail: React.FC<BeerBadgesRailProps> = ({
  variant,
  placement = 'overlay',
  availability,
  isInTastingList,
  onToggleTastingList,
  rating,
  showRatingWhenZero = false,
  demoRatingValue = 4.5,
  isFavourite,
  onToggleFavourite,
  favouriteAriaLabel,
  onOpenRating,
  onOpenNotes,
  notesHasContent,
  onAvailabilityClick,
}) => {
  const { getBadgeLayout, getAvailabilityBadgeStyle } = useAppSettings()
  const badgeLayout = getBadgeLayout()
  const availabilityBadgeStyle = getAvailabilityBadgeStyle()

  const isCompact = variant === 'list'

  const railSx = useMemo(() => {
    const direction = badgeLayout === 'vertical' ? 'column' : 'row'
    const gap = isCompact ? 0.5 : badgeLayout === 'vertical' ? 0.75 : 0.55

    return {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      flexDirection: direction,
      gap,
    }
  }, [badgeLayout, isCompact])

  const effectiveRating = useMemo(() => {
    if (typeof rating === 'number') {
      if (rating > 0) {
        return rating
      }

      if (showRatingWhenZero) {
        return demoRatingValue
      }

      return 0
    }

    if (showRatingWhenZero) {
      return demoRatingValue
    }

    return 0
  }, [demoRatingValue, rating, showRatingWhenZero])

  const shouldShowRating = showRatingWhenZero || effectiveRating > 0

  const orderedBadges = useMemo(() => {
    if (variant !== 'card') {
      return ['tasting_list', 'availability', 'rating', 'notes', 'favourite'] as const
    }

    if (badgeLayout === 'vertical') {
      if (placement === 'number_column') {
        return ['tasting_list', 'rating', 'favourite', 'availability'] as const
      }

      return ['availability', 'favourite', 'rating', 'tasting_list'] as const
    }

    return ['tasting_list', 'rating', 'favourite', 'availability'] as const
  }, [badgeLayout, placement, variant])

  return (
    <Box sx={railSx} className="BeerBadgesRail" data-badges-rail data-variant={variant}>
      {orderedBadges.map((badgeType) => {
        if (badgeType === 'tasting_list') {
          if (isInTastingList !== true) return null

          return (
            <Box
              key={badgeType}
              className="BeerBadgesRail__item BeerBadgesRail__item--tasting-list"
              data-badges-rail-item
              data-badges-rail-item-type="tasting_list"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation()
                onToggleTastingList?.()
              }}
              role={onToggleTastingList ? 'button' : undefined}
              aria-label={isInTastingList ? 'Remove from Tasting List' : 'Add to Tasting List'}
              sx={onToggleTastingList ? { cursor: 'pointer' } : undefined}
            >
              <StatusBadge
                type="tasting_list"
                isActive={isInTastingList}
                onClick={onToggleTastingList}
                variant={isCompact ? 'compact' : 'default'}
              />
            </Box>
          )
        }

        if (badgeType === 'availability') {
          if (!availability) return null

          return (
            <Box
              key={badgeType}
              className="BeerBadgesRail__item BeerBadgesRail__item--availability"
              data-badges-rail-item
              data-badges-rail-item-type="availability"
              data-badges-rail-item-availability={availability}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation()
                onAvailabilityClick?.()
              }}
              role={onAvailabilityClick ? 'button' : undefined}
              aria-label="Availability"
            >
              <StatusBadge
                type="availability"
                availability={availability}
                availabilityStyle={availabilityBadgeStyle}
                onClick={onAvailabilityClick}
                variant={isCompact ? 'compact' : 'default'}
              />
            </Box>
          )
        }

        if (badgeType === 'rating') {
          if (!shouldShowRating) return null

          return (
            <Box
              key={badgeType}
              className="BeerBadgesRail__item BeerBadgesRail__item--rating"
              data-badges-rail-item
              data-badges-rail-item-type="rating"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation()
                onOpenRating?.()
              }}
              role={onOpenRating ? 'button' : undefined}
              aria-label="Rate this beer"
              sx={onOpenRating ? { cursor: 'pointer' } : undefined}
            >
              {variant === 'list' ? (
                effectiveRating > 0 ? (
                  <StatusBadge type="rating" rating={effectiveRating} variant="compact" />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '28px',
                      height: '28px',
                      borderRadius: '10px',
                      backgroundColor: 'color-mix(in srgb, var(--secondary-colour) 86%, black)',
                      border: '1px solid var(--primary-colour)',
                      boxShadow: '0 2px 10px rgba(0,0,0,0.28)',
                    }}
                  >
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: 'rgba(255, 255, 255, 0.7)' }}>-</Typography>
                  </Box>
                )
              ) : (
                <StatusBadge type="rating" rating={effectiveRating} variant="default" />
              )}
            </Box>
          )
        }

        if (badgeType === 'notes') {
          if (variant !== 'list') return null

          return (
            <Box
              key={badgeType}
              className="BeerBadgesRail__item BeerBadgesRail__item--notes"
              data-badges-rail-item
              data-badges-rail-item-type="notes"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation()
                onOpenNotes?.()
              }}
              role="button"
              aria-label="Edit notes"
            >
              <StatusBadge type="notes" variant="compact" hasContent={Boolean(notesHasContent)} />
            </Box>
          )
        }

        if (badgeType === 'favourite') {
          if (typeof isFavourite !== 'boolean') return null

          return (
            <Box
              key={badgeType}
              className="BeerBadgesRail__item BeerBadgesRail__item--favourite"
              data-badges-rail-item
              data-badges-rail-item-type="favourite"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation()
                onToggleFavourite?.()
              }}
              role={onToggleFavourite ? 'button' : undefined}
              aria-label={favouriteAriaLabel ?? (isFavourite ? 'Unfavourite beer' : 'Favourite beer')}
              sx={onToggleFavourite ? { cursor: 'pointer' } : undefined}
            >
              <StatusBadge
                type="favourite"
                isActive={isFavourite}
                onClick={onToggleFavourite}
                variant={isCompact ? 'compact' : 'default'}
              />
            </Box>
          )
        }

        return null
      })}
    </Box>
  )
}

export default BeerBadgesRail
